import type { MapTriggerVolume, Tilemap2DTriggerVolume } from '../../content/schema/map';

/** A point to test trigger containment against, on a single floor. */
export interface TriggerProbePoint {
  x: number;
  z: number;
  floor: number;
}

/** A volume the point is inside/outside of, this call, respecting floor. */
function containsPoint(volume: MapTriggerVolume, point: TriggerProbePoint): boolean {
  if (volume.floor !== point.floor) return false;
  return (
    Math.abs(point.x - volume.center[0]) <= volume.size[0] / 2 &&
    Math.abs(point.z - volume.center[1]) <= volume.size[1] / 2
  );
}

/**
 * Diff a point's trigger-volume containment between two calls, mutating
 * `insideIds` (the previous-frame containment set) in place and returning
 * which volumes were newly entered/exited this call. Respects `floor` -
 * unlike the studio-layer HUD-feed containment check this generalises
 * (`useHudPlayState`'s `onPosition`, which only ever compared X/Z), a trigger
 * on a floor the player is not currently on is never considered entered.
 *
 * Pure and framework-agnostic: the caller owns `insideIds` (typically a ref)
 * and calls this once per tick/frame with the current point and the map's
 * trigger list.
 */
export function diffTriggerContainment(
  point: TriggerProbePoint,
  volumes: readonly MapTriggerVolume[],
  insideIds: Set<string>,
): { entered: MapTriggerVolume[]; exited: MapTriggerVolume[] } {
  const entered: MapTriggerVolume[] = [];
  const exited: MapTriggerVolume[] = [];
  const seen = new Set<string>();

  for (const volume of volumes) {
    const inside = containsPoint(volume, point);
    seen.add(volume.id);
    const was = insideIds.has(volume.id);
    if (inside && !was) {
      insideIds.add(volume.id);
      entered.push(volume);
    } else if (!inside && was) {
      insideIds.delete(volume.id);
      exited.push(volume);
    }
  }

  // A volume removed from the map entirely (deleted mid-session, or the
  // point's floor changed and it's no longer in the candidate list a caller
  // passes) should not leave a phantom "still inside" entry that can never
  // be exited again.
  for (const id of [...insideIds]) {
    if (!seen.has(id)) insideIds.delete(id);
  }

  return { entered, exited };
}

/**
 * The stable name a Logic rule's `event-trigger` condition matches against
 * for this volume: its authored `params.site` when set, else a positional
 * fallback. Must match `src/studio/editor/editor-modes2.tsx`'s `LogicRoute`
 * exactly (`triggerIds`, computed the same way from the project's map
 * triggers) - that is the list an author picks a trigger-condition value
 * from, so a different derivation here would mean an authored condition
 * silently never matches.
 */
export function triggerLabel(volume: MapTriggerVolume, allVolumes: readonly MapTriggerVolume[]): string {
  const site = volume.params?.site;
  if (typeof site === 'string' && site !== '') return site;
  const index = allVolumes.indexOf(volume);
  return `${volume.kind ?? 'trigger'}-${index}`;
}

/**
 * Convert a 2D map's own trigger volumes to the shared `MapTriggerVolume`
 * shape `diffTriggerContainment`/`ClientLogicRuntime.tick` operate on -
 * `floor`/`height` fixed at 0 since 2D has neither concept. This is what
 * lets `GameRuntime`'s 2D branch feed real containment into the same Logic
 * runtime the 3D branch already does, instead of the permanently-empty
 * array it used to pass (2D had no trigger concept to convert at all until
 * `Tilemap2DTriggerVolume` existed).
 */
export function tilemap2DTriggersToMapTriggerVolumes(
  triggers: readonly Tilemap2DTriggerVolume[],
): MapTriggerVolume[] {
  return triggers.map((t) => ({ ...t, floor: 0, height: 0 }));
}
